import { useLazyQuery } from "@apollo/client";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { Box, Skeleton, Typography } from "@mui/material";
import { fetchCategoryCounts, fetchTagListing } from "@platformx/authoring-apis";
import { useEffect, useRef, useState } from "react";
import TagCategorySection from "./TagCategorySection";
import {
  AllowedTagsByCategory,
  CategoryCounts,
  CategoryTagState,
  SelectedTagsByCategory,
  TagOption,
} from "./TagSelector.types";

type TagSelectorProps = {
  onChange?: (selected: SelectedTagsByCategory) => void;
  defaultSelected?: SelectedTagsByCategory;
  // Restricts which tags render for specific categories - see AllowedTagsByCategory. Categories
  // not present in the map render unrestricted, matching every existing caller's behavior.
  allowedTagsByCategory?: AllowedTagsByCategory;
  // Renders each category as a collapsed-by-default Accordion instead of always expanded.
  // Defaults to false so existing full-page pickers (e.g. content authoring's Tags field) keep
  // every category open; a compact picker (e.g. a filter modal) can opt into collapsed sections.
  collapsibleCategories?: boolean;
  // Floats these categories (in the given order) to the top of the list, ahead of every other
  // category. Categories not named here keep their relative order after the priority ones.
  priorityCategories?: string[];
  // Hides the remove ("x") affordance on already-selected chips for these categories - tags can
  // still be added, just not removed from this picker. For a category whose selection is a fixed
  // scope decided elsewhere (e.g. a prelem's allowed content types) rather than a free-form filter.
  lockedCategories?: string[];
  // Omits these categories entirely, regardless of allowedTagsByCategory - for a category that
  // makes no sense in this caller's context at all (e.g. content authoring's Tags field hiding
  // "Content Type"/"Collection", since a piece of content's type is fixed by the content itself).
  hiddenCategories?: string[];
};

const SKELETON_ROW_COUNT = 3;
const SKELETON_CHIP_COUNT = 12;
// Country/Player-sized categories can hold 20,000+ tags - fetch a small page per category
// instead of pulling everything, and let View More/search page further.
const TAGS_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const EMPTY_CATEGORY_STATE: CategoryTagState = {
  tags: [],
  nextStart: 0,
  hasMore: true,
  loading: true,
  loadingMore: false,
  searchTerm: "",
};

const TagSelector = ({
  onChange,
  defaultSelected,
  allowedTagsByCategory,
  collapsibleCategories = false,
  priorityCategories,
  lockedCategories,
  hiddenCategories,
}: TagSelectorProps) => {
  const [runFetchCategoryCounts, { loading: countsLoading }] = useLazyQuery(fetchCategoryCounts);

  const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<CategoryCounts>({});
  const [categoryState, setCategoryState] = useState<Record<string, CategoryTagState>>({});
  const [selected, setSelected] = useState<SelectedTagsByCategory>(defaultSelected ?? {});
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const searchDebounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Typing fast can leave two fetches for the same category in flight at once; network timing
  // doesn't guarantee the older search resolves first, so a stale response can otherwise
  // overwrite newer results/flicker back to an earlier state. Track the latest request per
  // category and drop any response that isn't from it.
  const latestRequestId = useRef<Record<string, number>>({});

  // Fetches one page of one category's tags. A fresh search (or the initial load) resets to
  // start: 0 and replaces the list; "View More" continues from nextStart and appends.
  const loadCategoryPage = (
    category: string,
    { searchTerm, start, replace }: { searchTerm: string; start: number; replace: boolean },
  ) => {
    const requestId = (latestRequestId.current[category] ?? 0) + 1;
    latestRequestId.current[category] = requestId;

    setCategoryState((prev) => ({
      ...prev,
      [category]: {
        ...(prev[category] ?? EMPTY_CATEGORY_STATE),
        loading: replace,
        loadingMore: !replace,
        searchTerm,
      },
    }));

    fetchTagListing({
      searchCategory: category,
      searchString: searchTerm,
      start,
      rows: TAGS_PAGE_SIZE,
    })
      .then((res: any) => {
        if (latestRequestId.current[category] !== requestId) return;
        const records = Array.isArray(res?.authoring_getTagsList) ? res.authoring_getTagsList : [];
        const newTags: TagOption[] = records
          .filter((record: any) => !!record?.tag_name)
          .map((record: any) => ({
            name: record.tag_name,
            value: record.tag_value || record.tag_name,
          }));
        setCategoryState((prev) => {
          const current = prev[category] ?? EMPTY_CATEGORY_STATE;
          const tags = replace ? newTags : [...current.tags, ...newTags];
          return {
            ...prev,
            [category]: {
              tags,
              nextStart: start + TAGS_PAGE_SIZE,
              // A page shorter than requested means the server has nothing left to give for
              // this category+search combination - real pagination end, not a total count
              // (authoring_categoryCounts' count is for the unfiltered category, not a search).
              hasMore: newTags.length === TAGS_PAGE_SIZE,
              loading: false,
              loadingMore: false,
              searchTerm,
            },
          };
        });
      })
      .catch(() => {
        if (latestRequestId.current[category] !== requestId) return;
        setCategoryState((prev) => ({
          ...prev,
          [category]: {
            ...(prev[category] ?? EMPTY_CATEGORY_STATE),
            loading: false,
            loadingMore: false,
          },
        }));
      });
  };

  useEffect(() => {
    runFetchCategoryCounts()
      .then((res) => {
        const counts: CategoryCounts = res?.data?.authoring_categoryCounts || {};
        const categories = Object.keys(counts);
        setCategoryCounts(counts);
        setCategoryOrder(categories);
        setCategoryState(
          Object.fromEntries(categories.map((category) => [category, { ...EMPTY_CATEGORY_STATE }])),
        );
        categories.forEach((category) =>
          loadCategoryPage(category, { searchTerm: "", start: 0, replace: true }),
        );
      })
      .catch(() => {})
      .finally(() => setHasLoadedOnce(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = !hasLoadedOnce && countsLoading;

  // A lockedCategory's selection is a fixed scope decided elsewhere (e.g. a prelem's allowed
  // content types); a hiddenCategory isn't even shown, so the user has no way to review or
  // re-add its selection if it got wiped. Neither should be touchable by a bulk "Clear All", nor
  // counted by the selected-count indicator as a user-made filter.
  const isProtectedCategory = (category: string) =>
    Boolean(lockedCategories?.includes(category) || hiddenCategories?.includes(category));

  const clearableSelectedCount = Object.entries(selected).reduce(
    (acc, [category, tags]) => (isProtectedCategory(category) ? acc : acc + tags.length),
    0,
  );

  const toggleTag = (category: string, tag: TagOption) => {
    setSelected((prev) => {
      const current = prev[category] ?? [];
      const exists = current.some((t) => t.value === tag.value);
      const next = exists ? current.filter((t) => t.value !== tag.value) : [...current, tag];
      const updated = { ...prev, [category]: next };
      onChange?.(updated);
      return updated;
    });
  };

  // Retains every protected category's selection (locked or hidden) - only clears categories the
  // user can otherwise freely toggle from this picker's own UI.
  const clearAll = () => {
    setSelected((prev) => {
      const cleared: SelectedTagsByCategory = Object.fromEntries(
        Object.entries(prev).filter(([category]) => isProtectedCategory(category)),
      );
      onChange?.(cleared);
      return cleared;
    });
  };

  const handleSearchChange = (category: string, value: string) => {
    setCategoryState((prev) => ({
      ...prev,
      [category]: { ...(prev[category] ?? EMPTY_CATEGORY_STATE), searchTerm: value },
    }));
    clearTimeout(searchDebounceTimers.current[category]);
    searchDebounceTimers.current[category] = setTimeout(() => {
      loadCategoryPage(category, { searchTerm: value, start: 0, replace: true });
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleViewMore = (category: string) => {
    const current = categoryState[category] ?? EMPTY_CATEGORY_STATE;
    loadCategoryPage(category, {
      searchTerm: current.searchTerm,
      start: current.nextStart,
      replace: false,
    });
  };

  if (isLoading) {
    return (
      <Box>
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, skeletonIndex) => (
          <Box key={skeletonIndex} sx={{ pb: 3 }}>
            <Skeleton variant='text' width={100} height={28} sx={{ mb: 1.5 }} />
            <Skeleton variant='rectangular' height={40} sx={{ mb: 1.5, borderRadius: "4px" }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {Array.from({ length: SKELETON_CHIP_COUNT }).map((_unused, chipIndex) => (
                <Skeleton
                  key={chipIndex}
                  variant='rectangular'
                  width={70 + ((chipIndex * 17) % 50)}
                  height={32}
                  sx={{ borderRadius: "16px" }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // A category present in allowedTagsByCategory with an empty list is hidden entirely (mirrors
  // the old GalleryLeftSideBar not rendering a category with no allowed tags); a category absent
  // from the map is unrestricted. Filtered out here (not just given zero tags) so the "Clear All"
  // header placement below still lands on the first category actually rendered.
  const visibleCategories = categoryOrder.filter((category) => {
    if (hiddenCategories?.includes(category)) return false;
    const restrictedNames = allowedTagsByCategory?.[category];
    return !(Array.isArray(restrictedNames) && restrictedNames.length === 0);
  });

  // Named categories float to the top (in the order given); everything else keeps its original
  // relative order after them.
  const orderedCategories = priorityCategories?.length
    ? [
        ...priorityCategories.filter((category) => visibleCategories.includes(category)),
        ...visibleCategories.filter((category) => !priorityCategories.includes(category)),
      ]
    : visibleCategories;

  return (
    <Box>
      {orderedCategories.map((category, index) => {
        const state = categoryState[category] ?? EMPTY_CATEGORY_STATE;
        const restrictedNames = allowedTagsByCategory?.[category];
        const isRestricted = Array.isArray(restrictedNames);
        const tags = isRestricted
          ? state.tags.filter((tag) => restrictedNames.includes(tag.name))
          : state.tags;
        const count = isRestricted ? restrictedNames.length : categoryCounts[category];
        return (
          <TagCategorySection
            key={category}
            category={category}
            tags={tags}
            count={count}
            selectedTags={selected[category] ?? []}
            onToggleTag={(tag) => toggleTag(category, tag)}
            searchTerm={state.searchTerm}
            onSearchChange={(value) => handleSearchChange(category, value)}
            loading={state.loading}
            loadingMore={state.loadingMore}
            hasMore={state.hasMore}
            onViewMore={() => handleViewMore(category)}
            collapsible={collapsibleCategories}
            removable={!lockedCategories?.includes(category)}
            restricted={isRestricted}
            headerRight={
              index === 0 && clearableSelectedCount > 0 ? (
                <Box
                  onClick={clearAll}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.8 },
                  }}>
                  <ClearAllIcon fontSize='small' />
                  <Typography variant='p3medium' sx={{ pl: "8px", color: "inherit" }}>
                    Clear All
                  </Typography>
                </Box>
              ) : undefined
            }
          />
        );
      })}
    </Box>
  );
};

export default TagSelector;
